import { LP_PRODUCT_HERO_BLOCK_KEYS } from "./lp-express-product-hero";

// Supported schema keywords only. Array maxima and anchor eligibility are
// enforced by SMB normalization rather than unsupported schema keywords.
const smbText = { type: "string", minLength: 1 } as const;
const smbOptionalText = { type: "string" } as const;
const smbObject = (properties: Record<string, unknown>) => ({
  type: "object", required: Object.keys(properties), properties,
});
const smbArray = (minItems: number, items: unknown = smbText) => ({
  type: "array", minItems, items,
});
const smbTitleBody = smbObject({ title: smbText, body: smbText });

export const LP_STUDIO_SMB_HOME_COPY_SCHEMA = smbObject({
  eyebrow: smbOptionalText,
  headline: smbText,
  subhead: smbText,
  primaryCtaText: smbText,
  primaryCtaSub: smbOptionalText,
  trustBar: smbObject({ label: smbText, items: smbArray(3) }),
  services: smbObject({
    heading: smbText, dek: smbText,
    items: smbArray(3, smbObject({ icon: smbText, title: smbText, body: smbText })),
  }),
  about: smbObject({ heading: smbText, body: smbText, highlights: smbArray(0) }),
  whyUs: smbObject({ heading: smbText, items: smbArray(3, smbTitleBody) }),
  proof: smbObject({
    heading: smbText, items: smbArray(0, smbObject({ quote: smbText, attribution: smbText })),
  }),
  process: smbObject({ heading: smbText, items: smbArray(3, smbTitleBody) }),
  serviceAreas: smbObject({ heading: smbText, dek: smbText, areas: smbArray(1) }),
  faq: smbObject({ heading: smbText, items: smbArray(3, smbObject({ q: smbText, a: smbText })) }),
  contact: smbObject({
    heading: smbText, dek: smbText, ctaText: smbText,
    phone: smbOptionalText, email: smbOptionalText, address: smbOptionalText, hours: smbOptionalText,
  }),
  footer: smbObject({ tagline: smbText, disclaimer: smbOptionalText }),
  nav: smbObject({ items: smbArray(2, smbObject({ label: smbText, anchor: smbText })) }),
});

export const LP_STUDIO_SMB_HOME_COPY_GUIDANCE =
  "Write plain, local, trustworthy home page copy using businessInfo facts only. " +
  "Use supplied service names and service areas verbatim and match the supplied primary CTA text and destination. " +
  "Do not invent prices, discounts, business age, licenses, certifications, awards, review counts, testimonials or guarantees. " +
  "Keep optional missing claims and contact details empty. With no supplied testimonials use proof.items = []. " +
  "Use neutral wording for missing non-factual copy, but never invent services, trust claims or areas to meet required minima. " +
  "Trust items: 3 to 6; services: 3 to 8; highlights: 0 to 4; whyUs: 3 to 6; proof: 0 to 6; " +
  "process: 3 to 5; areas: 1 to 20; FAQ: 3 to 8; nav: 2 to 6. " +
  "Nav anchors must be services, about, why-us, proof, process, areas, faq or contact. " +
  "Seeds describe structure only, not business facts. Do not use em dashes.";

export const LP_STUDIO_DESIGN_VERSION = 1 as const;

export const LP_STUDIO_IMAGE_BINDINGS = [
  "logo",
  "product",
  "lifestyle-1",
  "lifestyle-2",
] as const;

export type LpStudioImageBinding = (typeof LP_STUDIO_IMAGE_BINDINGS)[number];

export type LpStudioDesignSnapshot = {
  /** Absent on legacy Product Hero snapshots. Never inferred from live catalog. */
  rendererTemplateKey?: "product-hero" | "smb-home";
  version: typeof LP_STUDIO_DESIGN_VERSION;
  catalogTemplateKey: string;
  registeredTemplateId: string;
  html: string;
  /** Frozen renderer section configuration paired with this document. */
  sections?: unknown;
  // Immutable provider provenance copied from the Studio publication. This is
  // never inferred from a template name or a provider "recent designs" list.
  sourceMpEditorId?: string;
  sourceTemplateName?: string;
  // Absent for generic and historical pages. Present only when this exact
  // frozen document came from a compatible ready brand activation.
  variant?: {
    id: string;
    baselineFingerprint: string;
    designSystemId: string;
  };
};

/**
 * Resolve the durable renderer snapshot for deep-link/history hydration.
 * A terminal editor-backed result is authoritative over a stale duplicated
 * studioDesign HTML copy, but only when its exact editor/artifact provenance is
 * complete. Pending and failed attempts continue to render the frozen baseline.
 */
export function resolveLpStudioPreviewDesign(
  generatedContent: unknown,
): LpStudioDesignSnapshot | null {
  if (!generatedContent || typeof generatedContent !== "object") return null;
  const content = generatedContent as Record<string, any>;
  const design = isLpStudioDesignSnapshot(content.studioDesign)
    ? content.studioDesign
    : null;
  if (!design) return null;
  const result = content.studioBrandDesign;
  if (
    !result
    || typeof result !== "object"
    || result.status !== "ready"
    || typeof result.html !== "string"
    || !result.html.trim()
    || typeof result.sourceMpEditorId !== "string"
    || !result.sourceMpEditorId.trim()
    || typeof result.mpEditorId !== "string"
    || !result.mpEditorId.trim()
    || typeof result.mpArtifactId !== "string"
    || !result.mpArtifactId.trim()
    || typeof result.designSystemId !== "string"
    || !result.designSystemId.trim()
  ) return design;
  return {
    ...design,
    html: result.html,
    sections: result.sections ?? content.sections,
    variant: {
      id: [
        result.sourceMpEditorId,
        result.mpEditorId,
        result.mpArtifactId,
      ].join(":"),
      baselineFingerprint:
        typeof result.baselineFingerprint === "string"
          ? result.baselineFingerprint
          : typeof result.inputFingerprint === "string"
            ? result.inputFingerprint
            : design.variant?.baselineFingerprint || "",
      designSystemId: result.designSystemId,
    },
  };
}

// Template Studio's publication dialog starts with a complete Product Hero
// shape. Operators may change the words, but authored data-lp-bind paths must
// continue to resolve against this registration-time contract.
export const LP_STUDIO_DEFAULT_CONTENT_SEED = {
  eyebrow: "Featured product",
  badge: "Customer favorite",
  headline: "A clear product promise",
  subhead: "A concise explanation of why the product matters.",
  heroBullets: [
    "Primary customer benefit",
    "Secondary customer benefit",
    "Reason to believe",
  ],
  variants: {
    label: "Choose an option",
    options: ["Option one", "Option two", "Option three"],
  },
  ctaText: "Shop now",
  ctaSub: "Secure checkout",
  chips: ["Fast shipping", "Easy returns", "Secure checkout"],
  trust: { label: "Trusted by customers", items: ["Quality", "Service", "Results"] },
  benefits: {
    heading: "Why customers choose it",
    dek: "The most important outcomes at a glance.",
    items: [
      { ic: "sparkles", title: "Benefit one", body: "Supporting benefit detail." },
      { ic: "shield", title: "Benefit two", body: "Supporting benefit detail." },
      { ic: "heart", title: "Benefit three", body: "Supporting benefit detail." },
      { ic: "check", title: "Benefit four", body: "Supporting benefit detail." },
      { ic: "star", title: "Benefit five", body: "Supporting benefit detail." },
      { ic: "award", title: "Benefit six", body: "Supporting benefit detail." },
    ],
  },
  how: {
    heading: "How it works",
    items: [
      { title: "Step one", body: "First step detail." },
      { title: "Step two", body: "Second step detail." },
      { title: "Step three", body: "Third step detail." },
    ],
  },
  reviews: {
    heading: "What customers say",
    items: [
      { name: "Customer one", role: "Verified buyer", text: "Review placeholder.", rating: 5 },
      { name: "Customer two", role: "Verified buyer", text: "Review placeholder.", rating: 5 },
      { name: "Customer three", role: "Verified buyer", text: "Review placeholder.", rating: 5 },
    ],
  },
  guarantee: {
    sealTop: "Our",
    sealBottom: "Promise",
    heading: "Try it with confidence",
    body: "Guarantee details.",
  },
  faq: {
    heading: "Frequently asked questions",
    items: [
      { q: "Question one?", a: "Answer one." },
      { q: "Question two?", a: "Answer two." },
      { q: "Question three?", a: "Answer three." },
      { q: "Question four?", a: "Answer four." },
    ],
  },
  final: {
    heading: "Ready to get started?",
    dek: "A final reason to act now.",
    ctaText: "Shop now",
  },
  disclaimer: "Results and experiences may vary.",
  stickyCtaText: "Shop now",
} as const;

export const LP_STUDIO_DEFAULT_COPY_SCHEMA = {
  type: "object",
  required: [
    "eyebrow", "badge", "headline", "subhead", "heroBullets", "variantLabel",
    "variantOptions", "ctaText", "ctaSub", "chips", "trust", "benefits", "how",
    "reviews", "guarantee", "faq", "final", "disclaimer", "stickyCtaText",
  ],
  properties: {
    eyebrow: { type: "string", minLength: 1 },
    badge: { type: "string", minLength: 1 },
    headline: { type: "string", minLength: 1 },
    subhead: { type: "string", minLength: 1 },
    heroBullets: { type: "array", minItems: 3, items: { type: "string", minLength: 1 } },
    variantLabel: { type: "string", minLength: 1 },
    variantOptions: { type: "array", minItems: 3, items: { type: "string", minLength: 1 } },
    ctaText: { type: "string", minLength: 1 },
    ctaSub: { type: "string", minLength: 1 },
    chips: { type: "array", minItems: 3, items: { type: "string", minLength: 1 } },
    trust: {
      type: "object",
      required: ["label", "items"],
      properties: {
        label: { type: "string", minLength: 1 },
        items: { type: "array", minItems: 3, items: { type: "string", minLength: 1 } },
      },
    },
    benefits: {
      type: "object",
      required: ["heading", "dek", "items"],
      properties: {
        heading: { type: "string", minLength: 1 },
        dek: { type: "string", minLength: 1 },
        items: {
          type: "array",
          minItems: 6,
          items: {
            type: "object",
            required: ["ic", "title", "body"],
            properties: {
              ic: { type: "string", minLength: 1 },
              title: { type: "string", minLength: 1 },
              body: { type: "string", minLength: 1 },
            },
          },
        },
      },
    },
    how: {
      type: "object",
      required: ["heading", "items"],
      properties: {
        heading: { type: "string", minLength: 1 },
        items: {
          type: "array",
          minItems: 3,
          items: {
            type: "object",
            required: ["title", "body"],
            properties: {
              title: { type: "string", minLength: 1 },
              body: { type: "string", minLength: 1 },
            },
          },
        },
      },
    },
    reviews: {
      type: "object",
      required: ["heading", "items"],
      properties: {
        heading: { type: "string", minLength: 1 },
        items: {
          type: "array",
          minItems: 3,
          items: {
            type: "object",
            required: ["name", "role", "text", "rating"],
            properties: {
              name: { type: "string", minLength: 1 },
              role: { type: "string", minLength: 1 },
              text: { type: "string", minLength: 1 },
              rating: { type: "number" },
            },
          },
        },
      },
    },
    guarantee: {
      type: "object",
      required: ["sealTop", "sealBottom", "heading", "body"],
      properties: {
        sealTop: { type: "string", minLength: 1 },
        sealBottom: { type: "string", minLength: 1 },
        heading: { type: "string", minLength: 1 },
        body: { type: "string", minLength: 1 },
      },
    },
    faq: {
      type: "object",
      required: ["heading", "items"],
      properties: {
        heading: { type: "string", minLength: 1 },
        items: {
          type: "array",
          minItems: 4,
          items: {
            type: "object",
            required: ["q", "a"],
            properties: {
              q: { type: "string", minLength: 1 },
              a: { type: "string", minLength: 1 },
            },
          },
        },
      },
    },
    final: {
      type: "object",
      required: ["heading", "dek", "ctaText"],
      properties: {
        heading: { type: "string", minLength: 1 },
        dek: { type: "string", minLength: 1 },
        ctaText: { type: "string", minLength: 1 },
      },
    },
    disclaimer: { type: "string", minLength: 1 },
    stickyCtaText: { type: "string", minLength: 1 },
  },
} as const;
const PRODUCT_HERO_ROOTS = new Set<string>(
  LP_PRODUCT_HERO_BLOCK_KEYS as readonly string[],
);

function indexedBindings(
  prefix: string,
  count: number,
  fields?: readonly string[],
): string[] {
  return Array.from({ length: count }, (_, index) =>
    fields?.length
      ? fields.map((field) => `${prefix}.${index}.${field}`)
      : [`${prefix}.${index}`],
  ).flat();
}

/**
 * The exact populated copy leaves Template Studio may author. This list is
 * shared with provider prompts so authoring and publication cannot advertise
 * different array ranges.
 */
export const LP_STUDIO_COPY_BINDINGS = [
  ..."eyebrow badge headline subhead ctaText ctaSub variantLabel"
    .split(" ")
    .map((field) => `phBuyBox.${field}`),
  ...indexedBindings("phBuyBox.heroBullets", LP_STUDIO_DEFAULT_CONTENT_SEED.heroBullets.length),
  ...indexedBindings("phBuyBox.variantOptions", LP_STUDIO_DEFAULT_CONTENT_SEED.variants.options.length),
  ...indexedBindings("phBuyBox.chips", LP_STUDIO_DEFAULT_CONTENT_SEED.chips.length),
  "phTrustBar.label",
  ...indexedBindings("phTrustBar.items", LP_STUDIO_DEFAULT_CONTENT_SEED.trust.items.length),
  "phBenefits.heading",
  "phBenefits.dek",
  ...indexedBindings("phBenefits.items", LP_STUDIO_DEFAULT_CONTENT_SEED.benefits.items.length, ["ic", "title", "body"]),
  "phHowItWorks.heading",
  ...indexedBindings("phHowItWorks.items", LP_STUDIO_DEFAULT_CONTENT_SEED.how.items.length, ["title", "body"]),
  "phReviews.heading",
  ...indexedBindings("phReviews.items", LP_STUDIO_DEFAULT_CONTENT_SEED.reviews.items.length, ["name", "role", "text", "rating"]),
  ..."sealTop sealBottom heading body".split(" ").map((field) => `phGuarantee.${field}`),
  "phFaq.heading",
  ...indexedBindings("phFaq.items", LP_STUDIO_DEFAULT_CONTENT_SEED.faq.items.length, ["q", "a"]),
  ..."heading dek ctaText".split(" ").map((field) => `phFinalCta.${field}`),
  "phDisclaimer.text",
  "phStickyCta.ctaText",
] as const;

/**
 * Canonical bindings available only when a publication explicitly exposes the
 * corresponding schema and grounded seed. They are intentionally not part of
 * the default schema: requiring four specs for every product would encourage
 * providers to fabricate missing catalog facts.
 */
export const LP_STUDIO_OPTIONAL_COPY_BINDINGS = [
  "phBuyBox.productDetails.label",
  "phBuyBox.productDetails.heading",
  "phBuyBox.productDetails.description",
  ...indexedBindings("phBuyBox.productDetails.specs", 4, ["label", "value"]),
] as const;

const PRODUCT_HERO_TEXT_BINDINGS = new Set<string>([
  ...LP_STUDIO_COPY_BINDINGS,
  ...LP_STUDIO_OPTIONAL_COPY_BINDINGS,
]);

/**
 * Studio-authored copy bindings point directly at the existing Product Hero
 * generatedContent leaves, for example `phBuyBox.headline` or
 * `phBenefits.items.0.title`. Keeping the path explicit lets the normal LP
 * setText endpoint, snapshots, undo/redo, and regeneration remain authoritative.
 */
export function parseLpStudioBindingPath(
  raw: unknown,
): Array<string | number> | null {
  if (typeof raw !== "string") return null;
  const text = raw.trim();
  if (!text || text.length > 200) return null;
  if (!PRODUCT_HERO_TEXT_BINDINGS.has(text)) return null;
  const parts = text.split(".");
  if (!PRODUCT_HERO_ROOTS.has(parts[0])) return null;
  const path: Array<string | number> = [];
  for (const part of parts) {
    if (!part || !/^[A-Za-z][A-Za-z0-9_]*$|^(?:0|[1-9][0-9]*)$/.test(part)) {
      return null;
    }
    path.push(/^(?:0|[1-9][0-9]*)$/.test(part) ? Number(part) : part);
  }
  return path;
}

export function isLpStudioDesignSnapshot(
  value: unknown,
): value is LpStudioDesignSnapshot {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const design = value as Record<string, unknown>;
  return (
    design.version === LP_STUDIO_DESIGN_VERSION &&
    (design.rendererTemplateKey === undefined || design.rendererTemplateKey === "product-hero" || design.rendererTemplateKey === "smb-home") &&
    typeof design.catalogTemplateKey === "string" &&
    Boolean(design.catalogTemplateKey.trim()) &&
    typeof design.registeredTemplateId === "string" &&
    Boolean(design.registeredTemplateId.trim()) &&
    typeof design.html === "string" &&
      Boolean(design.html.trim()) &&
      (design.sourceMpEditorId === undefined ||
        (typeof design.sourceMpEditorId === "string" && Boolean(design.sourceMpEditorId.trim()))) &&
      (design.sourceTemplateName === undefined ||
        (typeof design.sourceTemplateName === "string" && Boolean(design.sourceTemplateName.trim()))) &&
      (
        design.variant === undefined ||
        (
          Boolean(design.variant) &&
          typeof (design.variant as Record<string, unknown>).id === "string" &&
          Boolean(String((design.variant as Record<string, unknown>).id).trim()) &&
          typeof (design.variant as Record<string, unknown>).baselineFingerprint === "string" &&
          /^[a-f0-9]{64}$/i.test(String((design.variant as Record<string, unknown>).baselineFingerprint)) &&
          typeof (design.variant as Record<string, unknown>).designSystemId === "string" &&
          Boolean(String((design.variant as Record<string, unknown>).designSystemId).trim())
        )
      )
  );
}

export function validateLpStudioCopyValue(value: unknown, schema: unknown, path = "$"): string | null {
  const rule = copySchemaObject(schema);
  if (!rule) return `${path} has an invalid schema`;
  const type = rule.type;
  if (type === "object") {
    const object = copySchemaObject(value);
    if (!object) return `${path} must be an object`;
    const properties = copySchemaObject(rule.properties) ?? {};
    const required = Array.isArray(rule.required) ? rule.required : [];
    for (const name of required) {
      if (typeof name === "string" && !(name in object)) return `${path}.${name} is required`;
    }
    for (const [name, childSchema] of Object.entries(properties)) {
      if (name in object) {
        const error = validateLpStudioCopyValue(object[name], childSchema, `${path}.${name}`);
        if (error) return error;
      }
    }
  } else if (type === "array") {
    if (!Array.isArray(value)) return `${path} must be an array`;
    if (typeof rule.minItems === "number" && value.length < rule.minItems) {
      return `${path} must contain at least ${rule.minItems} items`;
    }
    if (rule.items) {
      for (let index = 0; index < value.length; index++) {
        const error = validateLpStudioCopyValue(value[index], rule.items, `${path}[${index}]`);
        if (error) return error;
      }
    }
  } else if (type === "string") {
    if (typeof value !== "string") return `${path} must be a string`;
    if (typeof rule.minLength === "number" && value.trim().length < rule.minLength) {
      return `${path} must not be empty`;
    }
  } else if (type === "number" && typeof value !== "number") {
    return `${path} must be a number`;
  } else if (type === "boolean" && typeof value !== "boolean") {
    return `${path} must be a boolean`;
  }
  return null;
}

function copySchemaObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

export function validateLpStudioCopySchema(schema: unknown, path = "$", depth = 0): string | null {
  if (depth > 20) return `${path} exceeds the maximum schema depth`;
  const node = copySchemaObject(schema) as LpStudioCopySchemaNode | null;
  if (!node) return `${path} must be a schema object`;
  const allowed = new Set(["type", "required", "properties", "items", "minItems", "minLength"]);
  const unsupported = Object.keys(node).find((key) => !allowed.has(key));
  if (unsupported) return `${path}.${unsupported} is not supported`;
  if (!["object", "array", "string", "number", "boolean"].includes(String(node.type))) {
    return `${path}.type must be object, array, string, number, or boolean`;
  }
  if (node.minItems != null && (!Number.isInteger(node.minItems) || Number(node.minItems) < 0)) {
    return `${path}.minItems must be a non-negative integer`;
  }
  if (node.minLength != null && (!Number.isInteger(node.minLength) || Number(node.minLength) < 0)) {
    return `${path}.minLength must be a non-negative integer`;
  }
  if (node.minItems != null && node.type !== "array") return `${path}.minItems is only valid for arrays`;
  if (node.minLength != null && node.type !== "string") return `${path}.minLength is only valid for strings`;
  if (node.type === "object") {
    const properties = node.properties == null ? {} : copySchemaObject(node.properties);
    if (!properties) return `${path}.properties must be an object when provided`;
    if (node.required != null && !Array.isArray(node.required)) {
      return `${path}.required must be an array`;
    }
    for (const required of Array.isArray(node.required) ? node.required : []) {
      if (typeof required !== "string" || !(required in properties)) {
        return `${path}.required may contain only declared property names`;
      }
    }
    for (const [name, child] of Object.entries(properties)) {
      const error = validateLpStudioCopySchema(child, `${path}.properties.${name}`, depth + 1);
      if (error) return error;
    }
  }
  if (node.type === "array") {
    if (!node.items) return `${path}.items is required for arrays`;
    return validateLpStudioCopySchema(node.items, `${path}.items`, depth + 1);
  }
  return null;
}

function copySchemaPathForBinding(binding: string): Array<string | number> | null {
  const parts = binding.split(".");
  const root: Record<string, string> = {
    phBuyBox: "",
    phTrustBar: "trust",
    phBenefits: "benefits",
    phHowItWorks: "how",
    phReviews: "reviews",
    phGuarantee: "guarantee",
    phFaq: "faq",
    phFinalCta: "final",
    phDisclaimer: "disclaimer",
    phStickyCta: "stickyCtaText",
  };
  if (!(parts[0] in root)) return null;
  if (parts[0] === "phDisclaimer" && parts[1] === "text") return ["disclaimer"];
  if (parts[0] === "phStickyCta" && parts[1] === "ctaText") return ["stickyCtaText"];
  const prefix = root[parts[0]];
  const remainder = parts.slice(1).map((part) => /^\d+$/.test(part) ? Number(part) : part);
  return prefix ? [prefix, ...remainder] : remainder;
}

export function validateLpStudioCopyBindings(copyBindings: string[], schema: unknown): string | null {
  for (const binding of copyBindings) {
    const path = copySchemaPathForBinding(binding);
    if (!path) return `binding "${binding}" is unsupported`;
    let node = copySchemaObject(schema);
    let schemaPath = "$";
    for (const part of path) {
      if (!node) return `binding "${binding}" is not described by ${schemaPath}`;
      if (typeof part === "number") {
        if (node.type !== "array") return `binding "${binding}" requires ${schemaPath} to be an array`;
        if (typeof node.minItems !== "number" || node.minItems < part + 1) {
          return `binding "${binding}" requires ${schemaPath}.minItems to be at least ${part + 1}`;
        }
        node = copySchemaObject(node.items);
        schemaPath += `[${part}]`;
      } else {
        if (node.type !== "object") return `binding "${binding}" requires ${schemaPath} to be an object`;
        const properties = copySchemaObject(node.properties);
        const required = Array.isArray(node.required) ? node.required : [];
        if (!properties?.[part] || !required.includes(part)) {
          return `binding "${binding}" requires ${schemaPath}.${part} to be declared and required`;
        }
        node = copySchemaObject(properties[part]);
        schemaPath += `.${part}`;
      }
    }
    if (!node || !["string", "number"].includes(String(node.type))) {
      return `binding "${binding}" must resolve to a string or number`;
    }
    if (node.type === "string" && (!Number.isInteger(node.minLength) || Number(node.minLength) < 1)) {
      return `binding "${binding}" must require a nonempty string`;
    }
  }
  return null;
}

export const LP_STUDIO_DEFAULT_COPY_GUIDANCE =
  "Write concise, conversion-focused product-page copy grounded only in the supplied brand, product, persona, angle, and offer facts. Preserve every schema field. Do not invent prices, discounts, metrics, certifications, guarantees, or customer claims.";

type LpStudioCopySchemaNode = {
  type?: unknown;
  required?: unknown;
  properties?: unknown;
  items?: unknown;
  minItems?: unknown;
  minLength?: unknown;
};
